import React, {useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import makeStyles from '@mui/styles/makeStyles';
import {PATIENT_CONTEXT_TYPE} from '../../../../../util/patientContextUtil';
import {hasPermission, Permissions, sendUserAudit} from '../../../../../store/reducers/user';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {ctxPatientType} from '../../../../../store/reducers/patientContext';
import {additionalData} from '../../../../../store/reducers/additionalData';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {getConfig} from '../../../../../util/configUtil';
import {getECS} from '../../../forms/ecs/ecs';
import {setSnackbar} from '../../../../../store/reducers/globals';
import {currentMap} from '../../../../../store/reducers/ward';
import AgyleTextField from '../../../../../components/ui/fields/agyleTextField';
import SearchIcon from '@mui/icons-material/Search';
import MenuItem from '@mui/material/MenuItem';
import _ from 'lodash';
import Typography from '@mui/material/Typography';
import {getTableScrollbar} from '../../../../../util/scrollbar';
import clsx from 'clsx';
import { AUDIT_TYPES } from '../../../../../util/auditUtil';

const useStyles = makeStyles((theme) => {
    return {
        menuItem: {
            fontSize: 'small',
            width: '100%',
            minWidth: 100,
            display: 'flex',
            justifyContent: 'flex-start',
            '&:hover': {
                backgroundColor: theme.palette.background.paperDarker,
                borderRadius: 6,
            },
        },
        searchText: {
            height: 18,
        },
        textField: {
            backgroundColor: theme.palette.mode === 'light' ? '#e3e3e3' : '#434343',
        },
        menu: {
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            maxHeight: '70vh',
        },
    };
});

const MainScreenDocumentsButton = ({open, onClose, patient, anchorEl}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [documentsList, setDocumentsList] = useState([]);
    const [filterText, setFilterText] = useState('');

    // Selectors
    const patientType = useSelector(ctxPatientType);
    const patientAddData = useSelector(additionalData);
    const newCurrentMap = useSelector(currentMap);

    // Configs
    const enabledCustomPrescriptionFormButton = getConfig('customPrescriptionPrintForm', false);
    const enabledHVRFormButton = getConfig('healthVisitorReportsVisibleInDocuments', false);
    const enabledAccReferralFormButton = getConfig('accReferralForm', false);
    const enabledDvtReferralFormButton = getConfig('dvtReferralForm', false);
    const enabledAecsReferralFormButton = getConfig('aecsReferralForm', false);
    const enabledAmbulatoryCareFormButton = getConfig('ambulatoryCareForm', false);
    const enabledRoaENTFormButton = getConfig('roaENTForm', false);
    const enabledRoaEmergencyGynaeFormButton = getConfig('roaEmergencyGynaeForm', false);
    const enabledRoaEarlyPregnancyFormButton = getConfig('roaEarlyPregnancyForm', false);
    const enabledEDReferralFormButton = getConfig('emergencyDepartmentReferralForm', false);
    const enabledRoaRapidAccessChestPainFormButton = getConfig('roaRapidAccessChestPainForm', false);
    const enabledRoaMaxilloFormButton = getConfig('roaMaxilloForm', false);
    const enabledPneumothoraxFormButton = getConfig('pneumothoraxForm', false);
    const enabledFastTrackProtocolFormButton = getConfig('fastTrackProtocolForm', false);
    const enabledRespiratoryReferralFormButton = getConfig('respiratoryReferralForm', false);
    const enabledPrescriptionButton = getConfig('prescriptionEnabled', false);
    const enabledTtaDrugPrescriptionButton = getConfig('ttaDrugPrescriptionEnabled', false);
    const enabledCasCardLiteButton = getConfig('casCardLiteDischargeEnabled', false);
    const enabledNursingNotesButton = getConfig('nursingNotesEnabled', false);
    const enabledUnescortedFormButton = getConfig('unescortedForm', false);
    const enabledStickerButton = getConfig('sticker', false);
    const enabledMentalCapacityFormButton = getConfig('mentalCapacityForm', false);
    const enabledSelfDischargeFormButton = getConfig('selfDischargeForm', false);

    useEffect(() => {
        if (patientType !== PATIENT_CONTEXT_TYPE.TEMPORARY || dispatch(hasPermission(Permissions.CAN_ACCESS_ADMIN_PAGE))) {
            const documents = [];
            documents.push({
                name: 'cas card',
                display: t(patientType !== PATIENT_CONTEXT_TYPE.EDAU ? 'CAS CARD' : 'EDAU Documentation DoR'),
                icon: <PictureAsPdfIcon/>,
                width: 'auto',
                event: () => {
                    if (patientType !== PATIENT_CONTEXT_TYPE.EDAU)
                        createDorDocument('ecs');
                    else
                        createDorDocument('edauEcs');
                },
            });

            if (enabledCustomPrescriptionFormButton) {
                documents.push({
                    name: 'tta prescription',
                    display: t('TTA Prescription'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('customPrescriptionPrint'),
                });
            }

            if (enabledHVRFormButton) {
                documents.push({
                    name: 'health visitor reports',
                    display: t('Health Visitor Reports'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('healthVisitorReports'),
                });
            }

            if (enabledAccReferralFormButton) {
                documents.push({
                    name: 'acc referral form',
                    display: t('ACC Referral Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('accReferralForm'),
                });
            }

            if (enabledDvtReferralFormButton) {
                documents.push({
                    name: 'dvt referral form',
                    display: t('DVT Referral Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('dvtReferralForm'),
                });
            }

            if (enabledAecsReferralFormButton) {
                documents.push({
                    name: 'aecs Referral Form',
                    display: t('AECS Referral Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('aecsReferralForm'),
                });
            }

            if (enabledAmbulatoryCareFormButton) {
                documents.push({
                    name: 'ambulatory Care Form',
                    display: t('Ambulatory Care Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('ambulatoryCareForm'),
                });
            }

            if (enabledRoaENTFormButton) {
                documents.push({
                    name: 'roa ENTF orm',
                    display: t('ROA ENT Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('roaENTForm'),
                });
            }

            if (enabledRoaEmergencyGynaeFormButton) {
                documents.push({
                    name: 'roa Emergency Gynae Form',
                    display: t('ROA Emergency Gynae Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('roaEmergencyGynaeForm'),
                });
            }

            if (enabledRoaEarlyPregnancyFormButton) {
                documents.push({
                    name: 'roa Early Pregnancy Form',
                    display: t('ROA Early Pregnancy Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('roaEarlyPregnancyForm'),
                });
            }

            if (enabledEDReferralFormButton) {
                documents.push({
                    name: 'emergency Department Referral Form',
                    display: t('Emergency Department Referral Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('emergencyDepartmentReferralForm'),
                });
            }

            if (enabledRoaRapidAccessChestPainFormButton) {
                documents.push({
                    name: 'roa Rapid Access Chest Pain Form',
                    display: t('ROA Rapid Access Chest Pain Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('roaRapidAccessChestPainForm'),
                });
            }

            if (enabledRoaMaxilloFormButton) {
                documents.push({
                    name: 'roa Maxillo Form',
                    display: t('ROA Maxillo Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('roaMaxilloForm'),
                });
            }

            if (enabledPneumothoraxFormButton) {
                documents.push({
                    name: 'pneumothorax Form',
                    display: t('Pneumothorax Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('pneumothoraxForm'),
                });
            }

            if (enabledFastTrackProtocolFormButton) {
                documents.push({
                    name: 'fast Track Protocol Form',
                    display: t('Fast Track Protocol Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('fastTrackProtocolForm'),
                });
            }

            if (enabledRespiratoryReferralFormButton) {
                documents.push({
                    name: 'respiratory Referral Form',
                    display: t('Respiratory Referral Form'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('respiratoryReferralForm'),
                });
            }

            if (enabledPrescriptionButton) {
                documents.push({
                    name: 'prescription',
                    display: t('Prescription'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('prescription'),
                });
            }

            if (enabledTtaDrugPrescriptionButton) {
                documents.push({
                    name: 'ttA Drug Prescription',
                    display: t('TTA Drug pr.'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument(`tta.drug.prescription${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`),
                });
            }

            if (enabledCasCardLiteButton && !enabledNursingNotesButton) {
                documents.push({
                    name: 'cas card lite',
                    display: t('CAS CARD LITE'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('ecsLite'),
                });
            }

            if (enabledNursingNotesButton) {
                documents.push({
                    name: 'cas card lite',
                    display: t('CAS CARD LITE'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument(`nursing.notes${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`),
                });
            }

            if (enabledUnescortedFormButton) {
                documents.push({
                    name: 'unescorted Form',
                    display: t('Unescorted'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument(`unescorted.form${newCurrentMap?.mapPath === 'poole' ? 'PH' : 'RBH'}`),
                });
            }

            if (enabledStickerButton) {
                documents.push({
                    name: 'sticker',
                    display: t('Sticker'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('sticker'),
                });
            }

            if (enabledMentalCapacityFormButton) {
                documents.push({
                    name: 'mental Capacity Form',
                    display: t('Mental Capacity'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('mentalCapacity'),
                });
            }

            if (enabledSelfDischargeFormButton) {
                documents.push({
                    name: 'self Discharge Form',
                    display: t('Self Discharge'),
                    icon: <PictureAsPdfIcon/>,
                    event: () => createDorDocument('selfDischarge'),
                });
            }
            setDocumentsList(_.sortBy(documents, 'display'));
        } else {
            setDocumentsList([]);
        }
    }, [patientType, patientAddData, patientType, newCurrentMap]);

    const createDorDocument = (pdf) => {
        dispatch(sendUserAudit(patient, AUDIT_TYPES.OPEN_DOCUMENT, {pdfTemplate: pdf}));

        getECS(patient, patientAddData, patientType, true, pdf)
            .then((res) => {
                    if (!res) {
                        dispatch(setSnackbar({type: 'error', message: t('The document could not be generated.')}));
                    }
                },
            );
    };

    const handleCloseMenu = () => {
        setFilterText('');
        onClose();
    };

    const handleFilterChange = (event) => {
        event.preventDefault();
        if (!event.target.value && event.target.value.length === 0) {
            setFilterText('');
        } else {
            setFilterText(event.target.value);
        }
    };

    const filteredDocs = (array) => {
        return _.filter(array, t => _.toLower(t.display).includes(filterText?.toLowerCase()));
    };

    if (!open) return null;

    return (
        <Menu
            anchorEl={anchorEl}
            id="buttons-menu"
            open={open}
            onClose={handleCloseMenu}
            PaperProps={{
                elevation: 0,
                sx: {
                    overflow: 'visible',
                    p: 1,
                    borderRadius: '10px',
                    minWidth: 320,
                },
            }}
            BackdropProps={{
                invisible: true,
            }}
            transformOrigin={{horizontal: 'right', vertical: 'top'}}
            anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        >
            <AgyleTextField
                inputPropsClasses={classes.searchText}
                textFieldClass={classes.textField}
                size="small"
                fullWidth
                placeholder={t('Search documents')}
                variant="contained"
                onChange={handleFilterChange}
                value={filterText}
                noBorder={false}
                startAdornment={<SearchIcon style={{marginRight: 8}}/>}
                shrink
                smallerPlaceholder
                style={{marginBottom: 5}}
            />

            <Grid container direction="column" className={clsx(getTableScrollbar(), classes.menu)}>
                {filteredDocs(documentsList)?.map(opt => (
                    <MenuItem
                        key={opt?.name}
                        className={classes.menuItem}
                        onClick={() => {
                            opt.event();
                            onClose();
                        }}
                        disabled={opt.disabled || false}
                    >
                        {opt.icon}
                        <span style={{marginLeft: 5}}>
                            {opt?.display}
                        </span>
                    </MenuItem>
                ))}

                {filteredDocs(documentsList).length === 0 &&
                    <Typography variant="h6" textAlign="center" fontWeight="bold">
                        {t('No documents found')}
                    </Typography>
                }
            </Grid>
        </Menu>
    );
};

export default MainScreenDocumentsButton;